/**
 * MemberHistoryPanel
 * --------------------------------------------------------------------------
 * Unified change-history view for the Members page. Shows the audit trail
 * for the member itself and lets the user scope to any of their enrollments
 * (which previously had their own History tab inside EnrollmentDetail).
 */

import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import AuditTrailPanel from '@/components/common/AuditTrailPanel';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { membersAPI, Enrollment } from '@/services/api/members';

interface MemberHistoryPanelProps {
  memberId: number;
}

type Scope =
  | { kind: 'member' }
  | { kind: 'enrollment'; id: number; label: string };

export default function MemberHistoryPanel({ memberId }: MemberHistoryPanelProps) {
  const { t } = useAppTranslation();
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState(true);
  const [scope, setScope] = useState<Scope>({ kind: 'member' });

  useEffect(() => {
    let cancelled = false;
    setLoadingEnrollments(true);
    membersAPI
      .getMemberEnrollments(memberId)
      .then((rows) => {
        if (!cancelled) setEnrollments(rows || []);
      })
      .catch(() => {
        if (!cancelled) setEnrollments([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingEnrollments(false);
      });
    return () => {
      cancelled = true;
    };
  }, [memberId]);

  const handleScopeChange = (value: string) => {
    if (value === 'member') {
      setScope({ kind: 'member' });
      return;
    }
    const id = Number(value);
    const match = enrollments.find((e) => e.id === id);
    setScope({
      kind: 'enrollment',
      id,
      label: match?.program_name || `Enrollment #${id}`,
    });
  };

  const currentValue =
    scope.kind === 'member' ? 'member' : String(scope.id);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('members.showHistoryFor', 'Show history for:')}
        </label>
        <select
          value={currentValue}
          onChange={(e) => handleScopeChange(e.target.value)}
          disabled={loadingEnrollments}
          className="text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-gray-950 dark:focus:ring-gray-300"
        >
          <option value="member">{t('members.memberProfile', 'Member profile')}</option>
          {enrollments.map((e) => (
            <option key={e.id} value={e.id}>
              {t('members.enrollmentLabel', 'Enrollment')} — {e.program_name || `#${e.id}`}
            </option>
          ))}
        </select>
        {loadingEnrollments && (
          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        )}
      </div>

      {scope.kind === 'member' ? (
        <AuditTrailPanel
          entityType="member"
          entityId={memberId}
          apiPath={`/api/members/${memberId}/audit-log`}
        />
      ) : (
        <AuditTrailPanel
          key={scope.id}
          entityType="enrollment"
          entityId={scope.id}
          apiPath={`/api/enrollments/${scope.id}/audit-log`}
        />
      )}
    </div>
  );
}
